import messagesService from "api/messagesService";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PackagesContext from "contexts/PackagesContext";
import casesService from "api/casesService";
import { IWorkspace, IWorkspaceMember } from "dto/IWorkspace";
import NewInput from "components/NewInput";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import workspaceService from "api/workspaceService";
import Placeholder from "assets/placeholder.png";
import { useOnClickOutside } from "usehooks-ts";
import { generateNewKey } from "utils/geneareNewKey";
import { reEncrypt } from "utils/reEncrypt";
import { readAsBase64 } from "utils/readAsBase64";
import KeysContext from "contexts/KeysContext";
import { getKeys } from "utils/getKeys";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import NotFoundScreen from "components/NotFoundScreen";
import UserBadge from "components/UserBadge";

interface IItem {
  Id?: string;
  Date: Date;
  From: string;
  To: string;
  Privileges: number;
  ToUserId?: string;
}

const PackageEditSharesModal: FC<Props> = ({ item }) => {
  const { t } = useTranslation();
  const { keys } = useContext(KeysContext);
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const { data: sharesData, refetch: refetchShares } =
    messagesService.useGetShare(item.Id, item.Kind);
  const { mutate: removeShare } = messagesService.useRemoveShare();
  const { mutate: createShare } = messagesService.useShare();
  casesService.useGetCase(item.Id, !!item.Id?.length);
  const [active, setActive] = useState(false);
  const { packages, setPackages } = useContext(PackagesContext);
  const [shares, setShares] = useState<IItem[]>([]);

  const [workspaceId] = useState(getWorkspaceId() ?? "");

  const { data: workspaceData, isLoading } =
    workspaceService.useGetWorkspace(workspaceId);

  const workspace: IWorkspace = useMemo(
    () => (workspaceData?.data as IWorkspace) ?? [],
    [workspaceData],
  );

  const workspaceMembers = useMemo(() => workspace?.Members ?? [], [workspace]);

  const [searchedMembers, setSearchedMembers] = useState<IWorkspaceMember[]>(
    [],
  );

  const [searchValue, setSearchValue] = useState("");

  const onChoose = (member: IWorkspaceMember) => {
    handleShare(member);
  };

  const handleShare = async (member: IWorkspaceMember) => {
    const myKeys = await getKeys(userId!);
    const id = item.Id;
    const Kind = item.Kind;
    const encryptionKeyId = item.EncryptionKeyId;

    if (!encryptionKeyId) {
      createShare(
        {
          Id: id,
          Kind: Kind,
          ToUserId: member.UserId,
        },
        {
          onSuccess: () => {
            refetchShares();
          },
        },
      );

      return;
    }

    const privateKey = await generateNewKey(encryptionKeyId, myKeys!, keys);
    console.log(privateKey);
    const uint = await reEncrypt(userId, member.UserId, null, privateKey);
    const readyToSend = await readAsBase64(uint);

    createShare(
      {
        Id: id,
        Kind: Kind,
        ToUserId: member.UserId,
        Keys: [{ Id: encryptionKeyId!, Key: readyToSend }],
      },
      {
        onSuccess: () => {
          refetchShares();
          const newPacks = [...packages];
          const newPack = newPacks.find((p) => p.Id === item.Id);
          if (!newPack) {
            return;
          }

          newPack.IsShared = true;

          setPackages(newPacks);
        },
      },
    );
  };

  const outsideRef = useRef(null);

  useOnClickOutside(outsideRef, () => {
    if (active) {
      setActive(false);
    }
  });

  useEffect(() => {
    const activeShares = shares.map((x) => x.ToUserId);
    if (!searchValue.length) {
      setSearchedMembers(
        workspaceMembers.filter((w) => activeShares.indexOf(w.UserId) === -1),
      );
      return;
    }

    // filtracja

    setSearchedMembers(
      workspaceMembers.filter(
        (m) =>
          activeShares.indexOf(m.UserId) === -1 &&
          m.Label.toLocaleLowerCase().search(
            searchValue.toLocaleLowerCase(),
          ) !== -1,
      ),
    );
  }, [workspaceMembers, searchValue, shares]);

  useEffect(() => {
    if (sharesData) {
      setShares(
        sharesData.data.map((s) => {
          return {
            Id: s.Id,
            Date: s.Date,
            From: s.From,
            To: s.To,
            ToUserId: s.ToUserId,
            Privileges: 2,
          };
        }),
      );
    }
  }, [sharesData]);

  const handleDelete = (id: string) => {
    console.log("Handle delete");
    removeShare(
      {
        Id: id,
        Kind: item.Kind,
      },
      {
        onSuccess: () => {
          const newPacks = [...packages];
          const newPack = newPacks.find((p) => p.Id === item.Id);
          if (!newPack) {
            return;
          }

          newPack.IsShared = false;
          setPackages(newPacks);
          // toggle();
          refetchShares();
        },
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        {
          <div className={styles.search}>
            <NewInput
              focus
              labelClassName={styles.x}
              className={styles.d}
              value={searchValue}
              label={t("common.search")}
              onChange={(v) => setSearchValue(v)}
            />
          </div>
        }
        <div className={styles.shares}>
          {shares?.map((m) => (
            <UserBadge
              label={m.To}
              userId={m.ToUserId}
              allowRemove={true}
              onRemove={() => handleDelete(m.Id)}
            ></UserBadge>
          ))}
        </div>
        <div className={styles.list}>
          {searchedMembers
            .filter((member) => member.UserId)
            .map((member) => (
              <div
                onClick={() => {
                  setActive(false);
                  onChoose(member);
                }}
                key={member.Id}
                className={styles.box}
              >
                <img src={Placeholder} alt={member.Label} />
                {member.Label}
              </div>
            ))}
        </div>
        {searchedMembers.length === 0 && !isLoading && (
          <div className={styles.empty}>
            <NotFoundScreen
              title={t("emptyStates.notFound")}
              text={t("emptyStates.noResultsSuffix")}
              size="normal"
            ></NotFoundScreen>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackageEditSharesModal;
